const serializeElement = (element: HTMLElement) => {
  const cloned = element.cloneNode(true);
  if (cloned instanceof HTMLElement) {
    cloned.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  }
  return new XMLSerializer().serializeToString(cloned);
};

const createSvgDataUrl = (element: HTMLElement, width: number, height: number) => {
  const serialized = serializeElement(element);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
      <foreignObject width="100%" height="100%">
        ${serialized}
      </foreignObject>
    </svg>
  `;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const loadImage = (src: string, useCORS: boolean) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    if (useCORS) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = (event) => reject(event);
    image.src = src;
  });

const renderElementToCanvas = async (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const scale = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(rect.width * scale);
  canvas.height = Math.ceil(rect.height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to get canvas context.');
  }

  context.scale(scale, scale);

  const svgUrl = createSvgDataUrl(element, rect.width, rect.height);
  const image = await loadImage(svgUrl, true);
  context.drawImage(image, 0, 0);

  return canvas;
};

export const captureElementToPngBlob = async (
  element: HTMLElement
): Promise<Blob> => {
  const canvas = await renderElementToCanvas(element);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to capture screenshot.'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
};

export const copyBlobToClipboard = async (blob: Blob): Promise<void> => {
  if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
    throw new Error('Clipboard API is not available.');
  }
  const item = new ClipboardItem({ 'image/png': blob });
  await navigator.clipboard.write([item]);
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert blob to data URL.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read blob.'));
    reader.readAsDataURL(blob);
  });
